import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { AxisRendererRadial, CategoryAxis, RadarChart, RadarSeries, ValueAxis } from '@amcharts/amcharts4/charts';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-radar-chart',
  templateUrl: './radar-chart.component.html',
  styleUrls: ['./radar-chart.component.scss']
})
export class RadarChartComponent implements OnDestroy, AfterViewInit {
  public constructor(private ngZone: NgZone) {
  }

  static breakPoint: number = 580;
  static scaleSmall: number = 0.7;
  static scaleBig: number = 1;
  @ViewChild('chartTemplate', { static: true, read: ElementRef })
  public elementRef: ElementRef;

  @Input()
  public mode: string = 'default';

  @Input()
  public uuid: string;

  @Input()
  public dataLabels: string[];

  @Input()
  public showCursor: boolean = false;

  @Input()
  public showLegend: boolean = false;

  @Input()
  public firstSeriesColor: string = 'red';

  @Input()
  public secondSeriesColor: string = 'green';

  @Output()
  public axisSelected: EventEmitter<any> = new EventEmitter();

  public _data: any[] = [];
  @Input()
  public set data(newData: any[]) {
    this._data = newData;

    if (!environment['isLicenseSet']) {
      am4core.addLicense('CH204755581');
      environment['isLicenseSet'] = true;
    }

    if (this.chart) {
      this.chart.data = this._data;
    }
  }

  public chart: RadarChart = null;
  public categoryAxis: CategoryAxis = null;
  public valueAxis: ValueAxis<AxisRendererRadial> = null;
  public series: RadarSeries = null;
  public series2: RadarSeries = null;

  public ngAfterViewInit(): void {
    am4core.useTheme(am4themes_animated);
    this.chart = am4core.create(this.elementRef.nativeElement, am4charts.RadarChart);
    this.chart.data = this._data;

    this.categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
    this.categoryAxis.dataFields.category = 'item';

    this.valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
    this.series = this.chart.series.push(new am4charts.RadarSeries());
    this.series2 = this.chart.series.push(new am4charts.RadarSeries());

    let lastSelectedLabel: any;
    let lastSelectedRange: any;

    this.categoryAxis.renderer.labels.template.events.on('hit', (event: any) => {
      this.axisSelected.emit(event.target.dataItem.dataContext);
      const c = event.target.dataItem.dataContext.item;
      if (lastSelectedLabel) {
        lastSelectedLabel.fontWeight = 200;
      }
      if (lastSelectedRange) {
        lastSelectedRange.axisFill.fillOpacity = 0.0;
        lastSelectedRange.hide();
      }

      event.target.dataItem.label.fontWeight = 700;
      lastSelectedLabel = event.target.dataItem.label;

      const r = this.categoryAxis.axisRanges.create();
      r.category = c;
      r.endCategory = c;
      r.axisFill.fill = am4core.color('black');
      r.axisFill.fillOpacity = 1;
      r.locations.category = -0.01;
      r.locations.endCategory = 0.01;
      r.label.text = '';
      lastSelectedRange = r;
      this.chart.deepInvalidate();
    });

    this.categoryAxis.renderer.labels.template.wrap = true;
    this.categoryAxis.renderer.labels.template.maxWidth = 150;

    this.valueAxis.renderer.axisFills.template.fillOpacity = 0;
    this.valueAxis.renderer.labels.template.disabled = true;

    this.valueAxis.min = 0;
    this.valueAxis.extraMax = 0.111111111;

    if (this.mode === 'default') {
      const laag = this.valueAxis.axisRanges.create();
      laag.value = 13;
      laag.grid.strokeOpacity = 0;
      laag.label.inside = true;
      laag.label.text = 'basis';
      laag.label.zIndex = 1;
      laag.label.verticalCenter = 'middle';
      laag.label.horizontalCenter = 'middle';

      const basis = this.valueAxis.axisRanges.create();
      basis.value = 39;
      basis.grid.strokeOpacity = 0;
      basis.label.inside = true;
      basis.label.text = 'gemiddeld';
      basis.label.zIndex = 1;
      basis.label.verticalCenter = 'middle';
      basis.label.horizontalCenter = 'middle';

      const gevorderd = this.valueAxis.axisRanges.create();
      gevorderd.value = 71;
      gevorderd.grid.strokeOpacity = 0;
      gevorderd.label.inside = true;
      gevorderd.label.text = 'gevorderd';
      gevorderd.label.zIndex = 1;
      gevorderd.label.verticalCenter = 'middle';
      gevorderd.label.horizontalCenter = 'middle';

      const expert = this.valueAxis.axisRanges.create();
      expert.value = 90;
      expert.grid.strokeOpacity = 0;
      expert.label.inside = true;
      expert.label.text = 'expert';
      expert.label.zIndex = 1;
      expert.label.verticalCenter = 'middle';
      expert.label.horizontalCenter = 'middle';
    } else if (this.mode==="test") {
      this.valueAxis.max = 100;
      this.valueAxis.strictMinMax = true;
    }

    this.series.sequencedInterpolation = true;

    this.series.dataFields.valueY = 'valueLeft';
    this.series.dataFields.categoryX = 'item';
    this.series.name = this.dataLabels && this.dataLabels[0] ? this.dataLabels[0] : 'Left';
    this.series.stroke = am4core.color(this.firstSeriesColor);
    this.series.strokeWidth = 2;
    this.series.fillOpacity = 0.1;
    this.series.fill = am4core.color(this.firstSeriesColor);
    this.series.tensionX = 0.9;
    this.series.tensionY = 0.9;

    this.series2.dataFields.valueY = 'valueRight';
    this.series2.dataFields.categoryX = 'item';
    this.series2.name = this.dataLabels && this.dataLabels[1] ? this.dataLabels[1] : 'Right';
    this.series2.stroke = am4core.color(this.secondSeriesColor);
    this.series2.strokeWidth = 2;
    this.series2.fillOpacity = 0.1;
    this.series2.fill = am4core.color(this.secondSeriesColor);
    this.series2.tensionX = 0.9;
    this.series2.tensionY = 0.9;

    const label = this.categoryAxis.renderer.labels.template;
    label.wrap = true;
    label.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    label.maxWidth = 110;

    this.chart.events.on('sizechanged', () => {
      if (this.chart.measuredWidth < RadarChartComponent.breakPoint) {
        this.scaleChart(RadarChartComponent.scaleSmall);
      } else {
        this.scaleChart(RadarChartComponent.scaleBig);
      }
    });

    if (this.showCursor) {
      this.chart.cursor = new am4charts.RadarCursor();
    }
    if (this.showLegend) {
      this.chart.legend = new am4charts.Legend();
    }
  }

  public scaleChart: Function = (scale: number): void => {
    this.series.scale = scale;
    this.series2.scale = scale;
    this.valueAxis.scale = scale;
    this.categoryAxis.scale = scale;
    if (this.chart.legend) {
      this.chart.legend.scale = scale;
    }
  };

  public destroyGraph(): void {
    this.ngZone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyGraph();
  }
}
